/*
 * Central Chart.js setup for the analytics dashboard.
 * Import CHART_COLORS / option factories from here instead of registering
 * Chart.js scales or hardcoding palettes in individual components.
 */
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Mirrors the --an-chart-* tokens in tokens.css (Chart.js needs literal values)
export const CHART_COLORS = [
  "#2b5e9e", // brand blue
  "#e8863a", // orange
  "#1e9e6a", // green
  "#8a5cc4", // purple
  "#d64545", // red
  "#2a9db8", // teal
  "#c4478f", // magenta
  "#7a8699", // slate
];

export const TEXT_COLOR = "#51607a";
export const GRID_COLOR = "#eef1f6";
export const POSITIVE_COLOR = "#1e9e6a";
export const NEGATIVE_COLOR = "#d64545";

export const seriesColor = (index: number): string => CHART_COLORS[index % CHART_COLORS.length];

export const withAlpha = (hex: string, alpha: number): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Global visual defaults
ChartJS.defaults.font.family =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
ChartJS.defaults.font.size = 12;
ChartJS.defaults.color = TEXT_COLOR;

const sharedPlugins = {
  legend: {
    position: "bottom" as const,
    labels: {
      usePointStyle: true,
      pointStyle: "circle" as const,
      boxWidth: 8,
      padding: 16,
    },
  },
  tooltip: {
    backgroundColor: "#1a2333",
    titleColor: "#ffffff",
    bodyColor: "#d8dee9",
    padding: 10,
    cornerRadius: 6,
    boxPadding: 4,
    displayColors: true,
  },
};

export function lineOptions(overrides: ChartOptions<"line"> = {}): ChartOptions<"line"> {
  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: sharedPlugins,
    scales: {
      x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true } },
      y: { beginAtZero: true, grid: { color: GRID_COLOR }, border: { display: false } },
    },
    ...overrides,
  };
}

export function barOptions(overrides: ChartOptions<"bar"> = {}): ChartOptions<"bar"> {
  return {
    responsive: true,
    maintainAspectRatio: false,
    plugins: sharedPlugins,
    scales: {
      x: { grid: { display: false }, ticks: { maxRotation: 0, autoSkip: true } },
      y: { beginAtZero: true, grid: { color: GRID_COLOR }, border: { display: false } },
    },
    ...overrides,
  };
}

export function doughnutOptions(
  overrides: ChartOptions<"doughnut"> = {}
): ChartOptions<"doughnut"> {
  return {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "62%",
    plugins: sharedPlugins,
    ...overrides,
  };
}

// Dataset builders keep series styling consistent across tabs
export function lineDataset(label: string, data: number[], index = 0, filled = false) {
  const color = seriesColor(index);
  return {
    label,
    data,
    borderColor: color,
    backgroundColor: filled ? withAlpha(color, 0.12) : color,
    fill: filled,
    tension: 0.3,
    pointRadius: data.length > 45 ? 0 : 2.5,
    pointHoverRadius: 4,
    borderWidth: 2,
  };
}

export function barDataset(label: string, data: number[], index = 0) {
  const color = seriesColor(index);
  return {
    label,
    data,
    backgroundColor: withAlpha(color, 0.85),
    hoverBackgroundColor: color,
    borderRadius: 3,
    maxBarThickness: 36,
  };
}

export function doughnutDataset(data: number[]) {
  return {
    data,
    backgroundColor: data.map((_, i) => seriesColor(i)),
    borderColor: "#ffffff",
    borderWidth: 2,
  };
}

// Shared value formatters
export const formatCompact = (value: number): string =>
  Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(value);

export const formatCurrency = (value: number, digits = 2): string =>
  Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: digits,
  }).format(value);

export const formatPercent = (value: number, digits = 1): string => `${value.toFixed(digits)}%`;

export const formatMinutes = (minutes: number): string => {
  if (minutes < 60) return `${minutes.toFixed(1)} min`;
  return `${(minutes / 60).toFixed(1)} hrs`;
};
